import React, { useEffect, useRef, useState } from 'react';

/**
 * Compteur animé de 0 (ou from) vers `value` déclenché quand l'élément
 * entre dans le viewport. Utilise requestAnimationFrame + easing pour
 * un rendu fluide et un coût CPU minimal.
 *
 * v2 — Fiabilité renforcée :
 *   · Vérifie en synchrone à l'initialisation si l'élément est DÉJÀ visible
 *     (cas : stats au-dessus du fold) → évite l'état "figé à 30/75 %"
 *     capturé quand un screenshot se déclenche avant le premier scroll.
 *   · rootMargin négatif en bas pour ne déclencher que si l'élément est
 *     pleinement dans le tiers bas (évite déclenchement trop précoce).
 *   · Fallback : si IntersectionObserver indisponible → anime sur mount.
 *   · Respecte prefers-reduced-motion (affiche direct la valeur finale).
 *
 * @param {number} value - valeur cible
 * @param {number} from - valeur de départ (0)
 * @param {number} duration - durée en ms (1600)
 * @param {string} suffix - suffixe à afficher après (ex: '%', '°')
 * @param {number} decimals - nombre de décimales
 * @param {string} prefix - préfixe à afficher avant
 */
export function Counter({
  value = 0,
  from = 0,
  duration = 1600,
  suffix = '',
  prefix = '',
  decimals = 0,
  className = '',
  testid,
}) {
  // Pour les grandes valeurs (≥ 200), on initialise direct à la cible :
  //   · pas de flicker "0+" pendant que l'IntersectionObserver se réveille
  //   · pas d'animation count-up qui afficherait des valeurs intermédiaires
  //     (ex : "656" capté à mi-animation au lieu de "719")
  //   · l'œil ne peut de toute façon pas lire un compteur qui défile par
  //     centaines en moins d'une seconde
  const isLargeValue = Math.abs(value) >= 200;
  const [display, setDisplay] = useState(isLargeValue ? value : from);
  const ref = useRef(null);
  const triggered = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const runAnimation = () => {
      if (triggered.current) return;
      triggered.current = true;

      // Snap pour les grandes valeurs : pas d'animation
      if (isLargeValue) {
        setDisplay(value);
        return;
      }

      // Auto-ajuste la durée pour les valeurs intermédiaires (100-199)
      const absValue = Math.abs(value);
      const effectiveDuration = absValue >= 100 ? Math.min(duration, 800) : duration;

      const start = performance.now();
      const delta = value - from;

      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / effectiveDuration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        const current = from + delta * eased;
        setDisplay(current);
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    // Respect reduced-motion → affiche direct la valeur finale, pas d'animation
    const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq && mq.matches) {
      setDisplay(value);
      triggered.current = true;
      return;
    }

    // Fallback : si pas d'IntersectionObserver (ancien navigateur) → anime sur mount
    if (typeof IntersectionObserver === 'undefined') {
      runAnimation();
      return;
    }

    // 1) Vérification synchrone : l'élément est-il DÉJÀ dans le viewport ?
    //    (cas fréquent : stats au-dessus du fold chargées au mount)
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const isAlreadyVisible = rect.top < vh * 0.85 && rect.bottom > 0;

    if (isAlreadyVisible) {
      // Léger RAF pour laisser le premier paint se stabiliser (évite le "0" fantôme)
      requestAnimationFrame(() => runAnimation());
      return;
    }

    // 2) Sinon, on observe l'entrée progressive dans le viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runAnimation();
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -10% 0px', // déclenche quand l'élément est bien entré (évite tir trop précoce)
      }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, from, duration]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString('fr-FR');

  return (
    <span ref={ref} className={`c2r-counter ${className}`} data-testid={testid}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
